import React, { useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

export interface AttributeMapping {
  readonly sourceClaim: string;
  readonly targetProperty: 'email' | 'displayName' | 'groups' | 'role';
}

export interface AttributeMappingsStepProps {
  /** Current list of attribute mappings */
  readonly mappings: AttributeMapping[];
  /** Callback when mappings change */
  readonly onChange: (mappings: AttributeMapping[]) => void;
}

const TARGET_OPTIONS: Array<{ value: AttributeMapping['targetProperty']; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'displayName', label: 'Display Name' },
  { value: 'groups', label: 'Groups' },
  { value: 'role', label: 'Role' },
];

/**
 * Step 3 of the IdP Configuration Wizard.
 * Renders a repeatable field group for configuring attribute mappings
 * between source claims and target user properties.
 */
export function AttributeMappingsStep({ mappings, onChange }: AttributeMappingsStepProps): React.ReactElement {
  // Initialize with one empty mapping if the array is empty
  useEffect(() => {
    if (mappings.length === 0) {
      onChange([{ sourceClaim: '', targetProperty: 'email' }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSourceChange = (index: number, value: string): void => {
    const updated = mappings.map((mapping, i) =>
      i === index ? { ...mapping, sourceClaim: value } : mapping,
    );
    onChange(updated);
  };

  const handleTargetChange = (index: number, value: string): void => {
    const updated = mappings.map((mapping, i) =>
      i === index ? { ...mapping, targetProperty: value as AttributeMapping['targetProperty'] } : mapping,
    );
    onChange(updated);
  };

  const handleAdd = (): void => {
    onChange([...mappings, { sourceClaim: '', targetProperty: 'email' }]);
  };

  const handleRemove = (index: number): void => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  return (
    <fieldset className="space-y-4">
      <legend className="text-sm font-medium leading-none">Attribute Mappings</legend>

      {mappings.map((mapping, index) => (
        <div key={index} className="space-y-2 rounded-md border border-input p-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-2 space-y-1">
              <Label htmlFor={`source-claim-${index}`}>
                Source Claim
              </Label>
              <Input
                id={`source-claim-${index}`}
                type="text"
                maxLength={128}
                value={mapping.sourceClaim}
                onChange={(e) => handleSourceChange(index, e.target.value)}
              />
            </div>

            <div className="col-span-2 space-y-1">
              <Label htmlFor={`target-property-${index}`}>
                Target Property
              </Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                id={`target-property-${index}`}
                value={mapping.targetProperty}
                onChange={(e) => handleTargetChange(index, e.target.value)}
              >
                {TARGET_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-1 flex items-end">
              <Button
                type="button"
                variant="destructive"
                aria-label={`Remove mapping ${index + 1}`}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={handleAdd}
      >
        Add mapping
      </Button>
    </fieldset>
  );
}
